// src/features/chat/templates/receiptTemplates.js
/*
 * Templates for effect receipts — the PA saying "filed."
 * Receipts are past-tense factual; all text renders from server fields.
 */

function trimTrailingPeriod(text) {
  return text.replace(/[.。]\s*$/, '');
}

/**
 * Receipt card title (server-rendered summary).
 * @param {Object} receipt
 * @returns {string}
 */
export function receiptTitle(receipt) {
  const text = receipt?.summary || receipt?.notice?.text || 'Created a Record';
  return trimTrailingPeriod(text);
}

// Default states carry no information: a new private Record being "Private"
// or a new task being "Incomplete" is the world as expected, not a fact
// worth attention. Non-default values still render.
const DEFAULT_FIELD_NOISE = new Set(['privacy:private', 'status:incomplete']);

function isDefaultNoiseField(field) {
  const key = String(field.key || field.label || '').trim().toLowerCase();
  const value = String(field.value || '').trim().toLowerCase();
  return DEFAULT_FIELD_NOISE.has(`${key}:${value}`);
}

/**
 * Validated interpreted fields for the expanded card body.
 * @param {Object} receipt
 * @returns {Array<{key: string, label: string, value: string}>}
 */
export function receiptFields(receipt) {
  const fields = Array.isArray(receipt?.interpretedFields)
    ? receipt.interpretedFields
    : [];
  return fields
    .filter((field) => (
      field &&
      typeof field.label === 'string' &&
      field.label.trim() &&
      typeof field.value === 'string' &&
      field.value.trim() &&
      !isDefaultNoiseField(field)
    ))
    .map((field) => ({
      key: field.key || field.label,
      label: field.label,
      value: field.value,
    }));
}

/**
 * Record links carried by the receipt.
 * @param {Object} receipt
 * @returns {Array<{id: string, type: string}>}
 */
export function receiptRecords(receipt) {
  const records = Array.isArray(receipt?.resultRefs?.records)
    ? receipt.resultRefs.records
    : [];
  if (records.length) {
    return records
      .map((record) => ({
        id: record?.id || record?.recordId,
        type: record?.type || 'record',
      }))
      .filter((record) => record.id);
  }
  const recordIds = Array.isArray(receipt?.resultRefs?.recordIds)
    ? receipt.resultRefs.recordIds
    : [];
  return recordIds.map((id) => ({ id, type: 'record' })).filter((record) => record.id);
}

/**
 * Settled one-line whisper text.
 * @param {Object} receipt
 * @returns {string}
 */
export function receiptWhisperText(receipt) {
  return receiptTitle(receipt);
}

const UNDONE_TEXT_BY_OPERATION_KIND = {
  'record.create': 'Undone — removed the Record',
};

/**
 * Quietest trace of all — names what the undo did, not the original act.
 * @param {Object} receipt
 * @returns {string}
 */
export function undoneWhisperText(receipt) {
  return UNDONE_TEXT_BY_OPERATION_KIND[receipt?.operationKind] || 'Undone';
}

/**
 * Conversational seed offered once undo has lapsed: recovery stays one tap
 * from anywhere.
 * @returns {{label: string, text: string}}
 */
export function undoLapsedSeed() {
  return {
    label: 'Ask Affin to change this',
    text: 'Change the Record you created for me earlier',
  };
}
